import {DataStatus, FormField} from 'platform/components';
import {z} from 'zod';

import {isNil} from 'ramda';

import {useGetAssignableTagsQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {BulkOperationForm, type BulkOperationActionCallbackParams} from 'features/eag-grid';

const VehicleTagsBulkFormSchema = z.object({
  tagValueIds: z.array(z.string().uuid()).min(1, i18n.t('general.validations.fieldIsRequired')),
});

export function VehicleTagsBulkForm(props: {action: BulkOperationActionCallbackParams}) {
  const {
    data: tags,
    isLoading,
    isError,
  } = useGetAssignableTagsQuery({
    resourceId: EntityResourceIds.vehicle,
  });
  const field = props.action.paramsSchema?.root.elements.find(
    (candidate) => candidate.name === 'tagValueIds' && candidate.renderer === 'chips'
  );
  const tagLabels = new Map(
    tags?.flatMap((tag) => tag.values.map((value) => [value.id, value.name] as const))
  );
  const options = field?.defaultOptions?.map((option) => ({
    ...option,
    label: tagLabels.get(option.value) ?? option.label,
  }));
  return (
    <BulkOperationForm
      action={props.action}
      onError={handleApiError}
      experimentalZodSchema={VehicleTagsBulkFormSchema}
      defaultValues={{tagValueIds: []}}
      isDisabled={isNil(field) || isLoading || isError}
      toParams={(values) => ({tagValueIds: values.tagValueIds})}
    >
      {(control) => (
        <DataStatus isLoading={isLoading} isError={isError}>
          <FormField
            type="multiChoice"
            control={control}
            name="tagValueIds"
            label={field?.label ?? i18n.t('globalSearch.filters.tags')}
            options={options ?? []}
            menuInPortal
            isRequired
          />
        </DataStatus>
      )}
    </BulkOperationForm>
  );
}
